import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';

// ============================================================================
// API payload shapes for the Communication Dashboard. Property names are
// camelCase to match ASP.NET Core's default JSON serialization of the
// CommDashboard* DTOs. Date fields arrive as ISO strings (UTC, no offset).
// ============================================================================

export type Period = 'week' | 'month';

export interface CommDashboardSummaryDto {
	emailRequests: number;
	emailDelivered: number;
	emailOpened: number;
	emailClicked: number;
	emailBounces: number;
	emailSpamReports: number;
	emailUnsubscribed: number;
	emailDeliveredPct: number;
	emailOpenedPct: number;
	emailClickedPct: number;
	emailBouncesPct: number;
	emailSpamPct: number;
	emailUnsubscribedPct: number;
	smsSent: number;
	smsDelivered: number;
	smsUndelivered: number;
	smsFailed: number;
	smsDeliveredPct: number;
	smsUndeliveredPct: number;
	smsFailedPct: number;
}

export interface CommActivityPointDto {
	activityDate: string;
	emailSent: number;
	emailDelivered: number;
	emailOpened: number;
	emailClicked: number;
	emailBounced: number;
	smsSent: number;
	smsDelivered: number;
	smsUndelivered: number;
	smsFailed: number;
}

export interface CommEmployerDto {
	employerID: string;
	employerName: string;
	deliveryTypeID: number;
	commPreference: string;
	dripStartDate: string | null;
	censusLoaded: number;
	campaignsSent: number;
	campaignsTotal: number;
	lastCampaign: string | null;
	nextCampaign: string | null;
	emailDeliveredPct: number | null;
	smsDeliveredPct: number | null;
	hasMissingFields: boolean;
	hasIssues: boolean;
	stage: 'build' | 'staged' | 'active';
}

export interface CommCampaignDto {
	campaignID: string;
	campaignName: string;
	type: 'Single' | 'Drip';
	isScheduled: boolean;
	scheduleStatus: 'sent' | 'scheduled' | 'overdue' | 'paused';
	sendDate: string | null;
	emailSent: number;
	emailDeliveredPct: number | null;
	openedPct: number | null;
	clickedPct: number | null;
	bouncedPct: number | null;
	droppedPct: number | null;
	spamPct: number | null;
	smsSent: number;
	smsDeliveredPct: number | null;
	smsFailPct: number | null;
	alertHighBounce: boolean;
	alertHighSpam: boolean;
	alertHighDrop: boolean;
	alertHighDeferral: boolean;
	alertLowDelivery: boolean;
	alertHighSmsFailure: boolean;
	alertLowSmsDelivery: boolean;
	alertEmptyCensus: boolean;
}

export interface CommAlertDto {
	type: 'error' | 'warning' | 'info';
	category: string;
	message: string;
}

export interface CommEmployerDetailDto {
	employer: CommEmployerDto;
	allTime: CommDashboardSummaryDto;
	campaigns: CommCampaignDto[];
	alerts: CommAlertDto[];
}

@Injectable({ providedIn: 'root' })
export class CommDashboardService {

	constructor(private api: ApiService) {}

	getSummary(period: Period, employerId?: string): Observable<CommDashboardSummaryDto> {
		return this.api.get<CommDashboardSummaryDto>(`commdashboard/summary?${this.qs(period, employerId)}`);
	}

	getActivity(period: Period, employerId?: string): Observable<CommActivityPointDto[]> {
		return this.api.get<CommActivityPointDto[]>(`commdashboard/activity?${this.qs(period, employerId)}`);
	}

	getEmployers(): Observable<CommEmployerDto[]> {
		return this.api.get<CommEmployerDto[]>(`commdashboard/employers`);
	}

	getEmployerDetail(employerId: string): Observable<CommEmployerDetailDto> {
		return this.api.get<CommEmployerDetailDto>(`commdashboard/employers/${employerId}`);
	}

	private qs(period: Period, employerId?: string): string {
		const params = new URLSearchParams();
		params.set('period', period);
		if (employerId) {
			params.set('employerId', employerId);
		}
		return params.toString();
	}
}
